import styled from "styled-components";
import Button from "./Button";
import Card from "./Card";
import Input from "./Input";
import { Stack } from "./Layout";
import { Heading, Text } from "./Typography";
import { useState } from "react";
import { apiPost, ApiError } from "../services/apiClient";
import { clearSession } from "../services/authStorage";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(3)};
  background-color: ${({ theme }) => `${theme.colors.text}66`};
`;

const ModalCard = styled(Card)`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.modalMaxWidth};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const ErrorText = styled(Text)`
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

function DeleteAccountModal({ onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canConfirm = confirmText === "DELETE" && !loading;

  function handleClose() {
    setConfirmText("");
    setError("");
    onClose();
  }

  async function handleDelete() {
    if (!canConfirm) return;

    setLoading(true);
    setError("");
    try {
      await apiPost("/delete_user", {});
      clearSession();
      onDeleted();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Account deletion failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Overlay onClick={handleClose} role="presentation">
      <ModalCard
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        padding={4}
      >
        <Stack $gap={3}>
          <Heading as="h2" $size="xl" id="delete-modal-title">
            Delete your account?
          </Heading>
          <Text $muted>
            This action is permanent. Your account and associated data will be
            removed and cannot be recovered.
          </Text>
          <Text $size="sm">
            If you are sure about this, type DELETE into the entry box.
          </Text>
          <Input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
            disabled={loading}
            autoComplete="off"
          />
          {error && <ErrorText>{error}</ErrorText>}
          <Actions>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={!canConfirm}
            >
              {loading ? "Deleting..." : "Delete account"}
            </Button>
          </Actions>
        </Stack>
      </ModalCard>
    </Overlay>
  );
}

export default DeleteAccountModal;
